// Generate all k-combinations of arr
export function combinations(arr, k) {
  const results = [];
  function helper(start, combo) {
    if (combo.length === k) {
      results.push(combo.slice());
      return;
    }
    for (let i = start; i < arr.length; i++) {
      combo.push(arr[i]);
      helper(i + 1, combo);
      combo.pop();
    }
  }
  helper(0, []);
  return results;
}
// optimizer.js
// Extracted optimizer logic from keno-optimizer-app

/**
 * Generates balanced, unique keno cards from a set of numbers.
 * @param {number[]} masterSet - Array of selected numbers.
 * @param {number} subsetSize - Spots per card.
 * @param {number} numberOfSubsets - Number of cards to generate.
 * @returns {number[][]} Array of cards (each card is an array of numbers).
 */
export function generateBalancedSubset(masterSet, subsetSize, numberOfSubsets) {
  const unique = Array.from(new Set(masterSet)).sort((a,b) => a - b);
  const n = unique.length;
  const k = subsetSize;
  if (k <= 0 || n === 0 || k > n || numberOfSubsets <= 0) return [];

  const nCk = (nn, kk) => {
    if (kk < 0 || kk > nn) return 0;
    kk = Math.min(kk, nn - kk);
    let num = 1, den = 1;
    for (let i = 1; i <= kk; i++) {
      num *= (nn - (kk - i));
      den *= i;
    }
    return Math.round(num / den);
  };

  const maxCombos = nCk(n, k);
  const target = Math.min(numberOfSubsets, maxCombos);
  let counts = Object.fromEntries(unique.map(x => [x, 0]));
  const result = [];
  const seen = new Set();

  const makeCard = () => {
    const chosen = [];
    const chosenSet = new Set();
    for (let i = 0; i < k; i++) {
      const avail = unique.filter(x => !chosenSet.has(x));
      const minCount = Math.min(...avail.map(x => counts[x]));
      const pool = avail.filter(x => counts[x] === minCount);
      for (let j = pool.length - 1; j > 0; j--) {
        const r = Math.floor(Math.random() * (j + 1));
        [pool[j], pool[r]] = [pool[r], pool[j]];
      }
      const pick = pool[0];
      if (pick == null) return null;
      chosen.push(pick);
      chosenSet.add(pick);
    }
    chosen.sort((a,b) => a - b);
    const sig = chosen.join(',');
    if (seen.has(sig)) return null;
    return { chosen, sig };
  };

  const HARD_ATTEMPTS = 8000;
  const CARD_ATTEMPTS = 250;
  const BACKTRACK_STEPS = 2;
  const RESTARTS = 3;
  let attempts = 0, restarts = 0, stagnation = 0;

  while (result.length < target && attempts < HARD_ATTEMPTS) {
    attempts++;
    const candidate = makeCard();
    if (candidate) {
      result.push(candidate.chosen);
      seen.add(candidate.sig);
      for (const x of candidate.chosen) counts[x] += 1;
      stagnation = 0;
      continue;
    }
    stagnation++;
    if (stagnation % CARD_ATTEMPTS === 0 && result.length > 0) {
      const steps = Math.min(BACKTRACK_STEPS, result.length);
      for (let i = 0; i < steps; i++) {
        const last = result.pop();
        const sig = last.slice().sort((a,b) => a - b).join(',');
        seen.delete(sig);
        for (const x of last) counts[x] -= 1;
      }
      unique.push(unique.shift());
      continue;
    }
    if (stagnation > CARD_ATTEMPTS * 3 && restarts < RESTARTS) {
      restarts++;
      stagnation = 0;
      for (const key of Object.keys(counts)) counts[key] = 0;
      result.length = 0;
      seen.clear();
      for (let j = unique.length - 1; j > 0; j--) {
        const r = Math.floor(Math.random() * (j + 1));
        [unique[j], unique[r]] = [unique[r], unique[j]];
      }
    }
  }

  if (result.length < target && target < Infinity) {
    const isSeen = sig => seen.has(sig);
    const lexFill = () => {
      const a = unique;
      const idx = Array.from({ length: k }, (_, i) => i);
      const pushIfNew = () => {
        const combo = idx.map(i => a[i]).sort((x,y) => x - y);
        const sig = combo.join(',');
        if (!isSeen(sig)) {
          result.push(combo);
          seen.add(sig);
          for (const x of combo) counts[x] += 1;
          return true;
        }
        return false;
      };
      while (result.length < target) {
        if (!pushIfNew()) {}
        let i = k - 1;
        while (i >= 0 && idx[i] === i + a.length - k) i--;
        if (i < 0) break;
        idx[i]++;
        for (let j = i + 1; j < k; j++) idx[j] = idx[j - 1] + 1;
      }
    };
    lexFill();
  }
  return result.slice(0, target);
}
